// src/pages/payroll/PayrollListing.jsx
import { useEffect, useContext, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  Plus,
  RefreshCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  Hash,
  Type,
  Pencil,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const MONTHS = [
  { text: "Select", value: "Select" },
  { text: "January", value: "January" },
  { text: "February", value: "February" },
  { text: "March", value: "March" },
  { text: "April", value: "April" },
  { text: "May", value: "May" },
  { text: "June", value: "June" },
  { text: "July", value: "July" },
  { text: "August", value: "August" },
  { text: "September", value: "September" },
  { text: "October", value: "October" },
  { text: "November", value: "November" },
  { text: "December", value: "December" },
];

const YESNO = [
  { text: "Select", value: "Select" },
  { text: "Yes", value: "Yes" },
  { text: "No", value: "No" },
];

const PAYMODE = [
  { text: "Select", value: "Select" },
  { text: "Cash", value: "Cash" },
  { text: "Bank", value: "Bank" },
];

const PayrollListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || ""; // your login branch
  const branchType = localStorage.getItem("branchType") || "HO"; // HO / others

  const [showFilters, setShowFilters] = useState(true);

  // meta
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [years, setYears] = useState(["Select"]);
  const [columnVisibility, setColumnVisibility] = useState(null);

  // filters (ASP equivalents)
  const [branchId, setBranchId] = useState("Select");
  const [year, setYear] = useState("Select");
  const [month, setMonth] = useState("Select");
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [pf, setPf] = useState("Select");
  const [payMode, setPayMode] = useState("Select");
  const [esi, setEsi] = useState("Select");
  const [advance, setAdvance] = useState("Select");
  const [others, setOthers] = useState("Select");
  const [tds, setTds] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100); // ASP grid PageSize=100
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const codeTimer = useRef(null);
  const nameTimer = useRef(null);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-20", []);

  // ✅ consistent button colors
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  const canAddNew = String(branchType).toUpperCase() === "HO";

  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim()) {
      toast.warn("Login BranchId missing.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/payroll-listing/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: branchType,
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setBranches(res.data.branches || []);
      setDepartments(res.data.departments || []);
      setYears(res.data.years || ["Select"]);
      setColumnVisibility(res.data.columnVisibility || null);

      setBranchId(String(res.data.defaultBranchId || loginBranchId || "Select"));
      setYear(String(res.data.defaultYear || "Select"));
      setMonth(String(res.data.defaultMonth || "Select"));

      // reset other filters
      setPf("Select");
      setPayMode("Select");
      setEsi("Select");
      setAdvance("Select");
      setOthers("Select");
      setTds("Select");
      setDepartmentId("Select");
      setEmpCode("");
      setEmpName("");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || branchId === "Select") {
      toast.warn("Please select Branch");
      return;
    }
    if (!String(year).trim() || year === "Select") {
      toast.warn("Please select Year");
      return;
    }
    if (!String(month).trim() || month === "Select") {
      toast.warn("Please select Month");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/payroll-listing/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(branchId),
          year: String(year),
          month: String(month), // month name is fine
          empCode: empCode.trim(),
          empName: empName.trim(),
          pf,
          payMode,
          esi,
          advance,
          others,
          tds,
          departmentId,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setColumnVisibility(res.data.columnVisibility || columnVisibility);

        // reset selection on load
        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load payroll listing.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await loadMeta();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // after meta sets defaults, load list
  useEffect(() => {
    if (branchId !== "Select" && year !== "Select" && month !== "Select") {
      fetchList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, year, month]);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchList(page);
  };

  const handleAddNew = () => {
    // adjust route to your app
    navigate("/payroll/PayrollEntry");
  };

  const handleEdit = (payrollId) => {
    // ASP sets Session payrollID + year + month then redirects PayrollEditing.aspx
    // adjust route to your app
    navigate(`/payroll/PayrollEditing/${payrollId}?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => Number(r.fld_payrollid)));
    setSelectAll(true);
  };

  const handleDeleteSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }
    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/payroll/payroll-listing/remove`, {
        finYearId: Number(finYearId),
        payrollIds: selectedIds,
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Successfully Removed");
      } else {
        toast.error(res?.data?.message || "Remove failed");
      }

      const nextPage = page > 1 && rows.length === selectedIds.length ? page - 1 : page;
      setPage(nextPage);
      await fetchList(nextPage);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Remove failed");
    } finally {
      setLoading(false);
    }
  };

  // --- autocomplete calls ---
  const fetchEmpCodeSuggestions = async (prefix) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/payroll/payroll-listing/suggest/empcode`, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(branchId === "Select" ? loginBranchId : branchId),
          branchType,
          prefix,
        },
      });
      if (res?.data?.success) setCodeSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  const fetchEmpNameSuggestions = async (prefix) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/payroll/payroll-listing/suggest/empname`, {
        params: {
          finYearId: Number(finYearId),
          branchId: String(branchId === "Select" ? loginBranchId : branchId),
          branchType,
          prefix,
        },
      });
      if (res?.data?.success) setNameSug(res.data.items || []);
    } catch {
      // silent
    }
  };

  const onEmpCodeChange = (v) => {
    setEmpCode(v);
    if (codeTimer.current) clearTimeout(codeTimer.current);
    const t = String(v || "").trim();
    if (t.length < 1) {
      setCodeSug([]);
      return;
    }
    codeTimer.current = setTimeout(() => fetchEmpCodeSuggestions(t), 250);
  };

  const onEmpNameChange = (v) => {
    setEmpName(v);
    if (nameTimer.current) clearTimeout(nameTimer.current);
    const t = String(v || "").trim();
    if (t.length < 1) {
      setNameSug([]);
      return;
    }
    nameTimer.current = setTimeout(() => fetchEmpNameSuggestions(t), 250);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const vis = columnVisibility || {
    gradeVisible: true,
    paidBasicVisible: true,
    conveyanceVisible: true,
    educationalVisible: true,
    medicalVisible: true,
    hraVisible: true,
    otherAdditionsVisible: false,
    foodAllowanceVisible: false,
    mobileAllowanceVisible: false,
    exGratiaVisible: false,
    bonusVisible: false,
    leaveEncashmentVisible: false,
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PAYROLL LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Payroll Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Edit / Remove
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              {canAddNew && (
                <button onClick={handleAddNew} className={primaryBtn} disabled={loading}>
                  <Plus className="w-4 h-4" />
                  Add New
                </button>
              )}

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Employee Code */}
                <div>
                  <label className={labelBase}>Employee Code</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Hash className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <div className="w-full">
                      <input
                        value={empCode}
                        onChange={(e) => onEmpCodeChange(e.target.value)}
                        placeholder="Type employee code..."
                        className={inputBase}
                        list="empcode-list"
                      />
                      <datalist id="empcode-list">
                        {codeSug.slice(0, 20).map((x) => (
                          <option key={x} value={x} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </div>

                {/* Employee Name */}
                <div>
                  <label className={labelBase}>Employee Name</label>
                  <div className="mt-2 flex items-center gap-2">
                    <Type className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                    <div className="w-full">
                      <input
                        value={empName}
                        onChange={(e) => onEmpNameChange(e.target.value)}
                        placeholder="Type employee name..."
                        className={inputBase}
                        list="empname-list"
                      />
                      <datalist id="empname-list">
                        {nameSug.slice(0, 20).map((x) => (
                          <option key={x} value={x} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                </div>

                {/* Year */}
                <div>
                  <label className={labelBase}>Year</label>
                  <select className={`${inputBase} w-full`} value={year} onChange={(e) => setYear(e.target.value)} disabled={loading}>
                    {(years || ["Select"]).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month */}
                <div>
                  <label className={labelBase}>Month</label>
                  <select className={`${inputBase} w-full`} value={month} onChange={(e) => setMonth(e.target.value)} disabled={loading}>
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.text}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Branch */}
                <div>
                  <label className={labelBase}>Branch</label>
                  <select className={`${inputBase} w-full`} value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={loading}>
                    <option value="Select">Select</option>
                    {branches.map((b, i) => {
                      // try common shapes from SP bind ddl: either (id/name) or (fld_branchid/fld_branchname)
                      const id = String(b.id ?? b.fld_Branchid ?? b.value ?? b.BranchId ?? "");
                      const name = String(b.name ?? b.fld_BranchName ?? b.text ?? b.BranchName ?? id);
                      return (
                        <option key={`${id}-${i}`} value={id}>
                          {name}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Department */}
                <div>
                  <label className={labelBase}>Department</label>
                  <select
                    className={`${inputBase} w-full`}
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    {departments.map((d, i) => (
                      <option key={`${d.fld_deptid}-${i}`} value={String(d.fld_deptid)}>
                        {String(d.fld_departmentname)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* PF */}
                <div>
                  <label className={labelBase}>PF</label>
                  <select className={`${inputBase} w-full`} value={pf} onChange={(e) => setPf(e.target.value)} disabled={loading}>
                    {YESNO.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ESI */}
                <div>
                  <label className={labelBase}>ESI</label>
                  <select className={`${inputBase} w-full`} value={esi} onChange={(e) => setEsi(e.target.value)} disabled={loading}>
                    {YESNO.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Payment Mode */}
                <div>
                  <label className={labelBase}>Payment Mode</label>
                  <select className={`${inputBase} w-full`} value={payMode} onChange={(e) => setPayMode(e.target.value)} disabled={loading}>
                    {PAYMODE.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Advance */}
                <div>
                  <label className={labelBase}>Advance</label>
                  <select className={`${inputBase} w-full`} value={advance} onChange={(e) => setAdvance(e.target.value)} disabled={loading}>
                    {YESNO.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Others */}
                <div>
                  <label className={labelBase}>Others</label>
                  <select className={`${inputBase} w-full`} value={others} onChange={(e) => setOthers(e.target.value)} disabled={loading}>
                    {YESNO.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>

                {/* TDS */}
                <div>
                  <label className={labelBase}>TDS</label>
                  <select className={`${inputBase} w-full`} value={tds} onChange={(e) => setTds(e.target.value)} disabled={loading}>
                    {YESNO.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.text}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await loadMeta();
                    setPage(1);
                  }}
                  className={ghostBtn}
                  disabled={loading}
                >
                  <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Reset
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">S.No</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Employee Code</th>
                    <th className="px-4 py-4 text-left font-bold whitespace-nowrap">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Designation</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Branch Name</th>
                    {vis.gradeVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Grade</th>}
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">LOP</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Basic Pay</th>
                    {vis.paidBasicVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Paid Basic</th>}
                    {vis.conveyanceVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Conveyance</th>}
                    {vis.educationalVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Educational</th>}
                    {vis.medicalVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Medical</th>}
                    {vis.hraVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">HRA</th>}
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">PF</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">ESI</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Advance</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">LOP Amount</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">TDS</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Other Deductions</th>

                    {/* columns that ASP hard-hides — keep off unless you want to show */}
                    {vis.otherAdditionsVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Other Additions</th>}
                    {vis.foodAllowanceVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Food Allowance</th>}
                    {vis.mobileAllowanceVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Mobile Allowance</th>}
                    {vis.exGratiaVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Ex.Gratia</th>}
                    {vis.bonusVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Bonus</th>}
                    {vis.leaveEncashmentVisible && <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Leave Encashment</th>}

                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Net Pay</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">Edit</th>
                    <th className="px-4 py-4 text-center font-bold whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: Math.min(pageSize, 10) }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td colSpan={25} className="px-4 py-6">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={25} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const payrollId = Number(r.fld_payrollid);
                      const lopDays = String(r.fld_lopdays ?? r.fld_lop ?? "0");
                      const isLop = lopDays !== "0";

                      const rowCls = isLop
                        ? "bg-red-600/25"
                        : `${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`;

                      return (
                        <tr key={payrollId || idx} className={rowCls}>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_employeecode}
                          </td>

                          <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_employee}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_designationname}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_branchname}
                          </td>

                          {vis.gradeVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_gradename}
                            </td>
                          )}

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {lopDays}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_basicpay}
                          </td>

                          {vis.paidBasicVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_paidbasic}
                            </td>
                          )}

                          {vis.conveyanceVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_conveyance}
                            </td>
                          )}

                          {vis.educationalVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_educational}
                            </td>
                          )}

                          {vis.medicalVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_medical}
                            </td>
                          )}

                          {vis.hraVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_hra}
                            </td>
                          )}

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_pf}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_esi}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_advance}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_lopamt}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_tds}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_others}
                          </td>

                          {vis.otherAdditionsVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_othersaddition}
                            </td>
                          )}
                          {vis.foodAllowanceVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_foodallowance}
                            </td>
                          )}
                          {vis.mobileAllowanceVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_mobileallowance}
                            </td>
                          )}
                          {vis.exGratiaVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_exgratia}
                            </td>
                          )}
                          {vis.bonusVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_bonus}
                            </td>
                          )}
                          {vis.leaveEncashmentVisible && (
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                              {r.fld_leaveencashment}
                            </td>
                          )}

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {r.fld_Netpay}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => handleEdit(payrollId)}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-2xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all shadow"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(payrollId)}
                              onChange={() => toggleSelect(payrollId)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination + delete */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={handleDeleteSelected} disabled={selectedIds.length === 0} className={dangerBtn}>
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default PayrollListing;
